/**
 * Dashboard shell: top bar with the brand, a theme switcher (light / dark /
 * high-contrast, persisted to localStorage — mirrors oakallow's Header), the
 * signed-in user, and sign-out. Renders nested routes via <Outlet />.
 *
 * Token-themed throughout: var(--…) + .btn classes, no hardcoded colors/fonts.
 */

import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { Sun, Moon, Eye, LogOut } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';

type Theme = 'light' | 'dark' | 'high-contrast';

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!showThemeMenu) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowThemeMenu(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [showThemeMenu]);

  const handleTheme = (theme: Theme) => {
    document.documentElement.classList.remove('dark', 'high-contrast');
    if (theme === 'dark') document.documentElement.classList.add('dark');
    else if (theme === 'high-contrast') document.documentElement.classList.add('high-contrast');
    localStorage.setItem('theme', theme);
    setShowThemeMenu(false);
  };

  const initial = (user?.display_name || user?.email || '?').slice(0, 1).toUpperCase();

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1.25rem',
          borderBottom: '1px solid var(--border-color)',
          background: 'var(--bg-secondary)',
        }}
      >
        <div style={{ fontSize: 18 }}>
          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Sylvan </span>
          <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>Authority</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ position: 'relative' }} ref={menuRef}>
            <button
              onClick={() => setShowThemeMenu((v) => !v)}
              className="btn btn-secondary btn-sm"
              aria-label="Change theme"
            >
              <Sun size={16} />
            </button>
            {showThemeMenu && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: '0.25rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--card-shadow)',
                  overflow: 'hidden',
                  zIndex: 50,
                  minWidth: 150,
                }}
              >
                {([
                  { theme: 'light', icon: Sun, label: 'Light' },
                  { theme: 'dark', icon: Moon, label: 'Dark' },
                  { theme: 'high-contrast', icon: Eye, label: 'High Contrast' },
                ] as { theme: Theme; icon: typeof Sun; label: string }[]).map(({ theme, icon: Icon, label }) => (
                  <button
                    key={theme}
                    onClick={() => handleTheme(theme)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      width: '100%',
                      border: 'none',
                      background: 'none',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                    }}
                  >
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              background: 'var(--brand-light)',
              color: 'var(--brand-dark)',
              fontSize: 13,
              fontWeight: 600,
            }}
            title={user?.email ?? ''}
          >
            {initial}
          </div>
          <button onClick={() => void logout()} className="btn btn-secondary btn-sm" aria-label="Sign out">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main style={{ padding: '1.5rem', maxWidth: 1100, margin: '0 auto' }}>
        <Outlet />
      </main>
    </div>
  );
}
